import type { ReactNode } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

import { Card, EmptyState } from '@/components/ui';
import type { Profile } from '@/types';

import { RequireAuth } from './RequireAuth';
import { useAuth } from './providers/authContext';

export interface RequireProfileProps {
  /** Profiles admitted on this route; anything else gets the "sem permissão" state. */
  allow: readonly Profile[];
  children?: ReactNode | undefined;
  redirectTo?: string | undefined;
}

function ProfileGate({ allow, children }: Pick<RequireProfileProps, 'allow' | 'children'>) {
  const { profile } = useAuth();

  if (profile !== null && allow.includes(profile)) {
    return <>{children ?? <Outlet />}</>;
  }

  // A profile outside the list is sent to its own home, not shown a "sem permissão" page: it
  // reaches here by a stale link, a typed URL, or — the case that exposed it — signing in right
  // after another profile signed out on a page of its own, whose path RequireAuth had kept as
  // the destination. None of those is a reason to show someone an area that is not theirs.
  if (profile !== null) {
    return <Navigate to="/" replace />;
  }

  return (
    <Card elevation="raised" padding="none">
      <EmptyState
        title="Sem permissão para esta área"
        icon="⛔"
        description="Seu usuário ainda não tem um perfil de acesso. Fale com o administrador da sua organização."
      />
    </Card>
  );
}

/**
 * Authorisation guard, composed on top of `RequireAuth` rather than repeating it: a signed-out
 * user must still be sent to `/login` with the intended destination preserved. A signed-in profile
 * outside `allow` goes to its own home; only a session with no recognised profile — which has no
 * home to go to — sees the "sem permissão" state.
 *
 * The check is a usability affordance, not the security control. The API re-verifies the profile
 * claim on every request and Row Level Security filters the rows, so bypassing this component
 * yields an empty screen and a 403, never data.
 */
export function RequireProfile({ allow, children, redirectTo }: RequireProfileProps) {
  return (
    <RequireAuth {...(redirectTo === undefined ? {} : { redirectTo })}>
      <ProfileGate allow={allow}>{children}</ProfileGate>
    </RequireAuth>
  );
}
